import { LanguageInput } from "@/components/LanguageInput";
import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import { getLanguageName, getProficiencyLevel, Language } from "@/utils/language";
import usePagination from "@/utils/usePagination";
import useUrlParam from "@/utils/useUrlParam";
import { ChangeEvent, FormEvent, useEffect, useMemo } from "react";
import { User } from "snudbud-backbone-sdk";
import { useAPI } from "../contexts/APIContext";
import handleError from "../utils/handleError";

/**
 * Search page that allows users to find language partners
 */
export default function SearchPage() {
  const { api, userProfile, user } = useAPI();
  const isLoggedIn = !!user?.userId;

  // Consolidated URL parameters
  const [query, setQuery] = useUrlParam<string>("query", "");
  const [languageCode, setLanguageCode] = useUrlParam<string>(
    "languageCode",
    userProfile?.languages?.[0]?.languageCode || ""
  );
  const [learning, setLearning] = useUrlParam<boolean>("learning", true);
  const [minProficiency, setMinProficiency] = useUrlParam<string>("minProf", "");
  const [maxProficiency, setMaxProficiency] = useUrlParam<string>("maxProf", "");
  const [limit, setLimit] = useUrlParam<number>("limit", 10);
  const [offset, setOffset] = useUrlParam<number>("offset", 0);
  const [searchType, setSearchType] = useUrlParam<string>("type", "learners");
  const [advancedMode, setAdvancedMode] = useUrlParam<boolean>("advanced", false);

  // Apply search type settings
  useEffect(() => {
    if (advancedMode) return;

    if (searchType === "natives") {
      setLearning(false);
      setMinProficiency("0.8");
      setMaxProficiency("");
    } else {
      setLearning(true);
      setMinProficiency("");
      setMaxProficiency("");
    }
  }, [searchType, advancedMode, setLearning, setMinProficiency, setMaxProficiency]);

  // Search pagination
  const searchResults = usePagination<User.SearchReturn["users"][0]>({
    fn: async ({ offset, limit }) => {
      try {
        // If not logged in, still run the search but we'll hide the results
        const searchParams: Record<string, any> = { offset, limit };

        if (query) searchParams.query = query;
        if (languageCode) searchParams.languageCode = languageCode;
        if (learning !== undefined) searchParams.learning = learning;
        if (minProficiency) searchParams.minProficiency = parseFloat(minProficiency);
        if (maxProficiency) searchParams.maxProficiency = parseFloat(maxProficiency);

        const results = await api.user.search(searchParams);
        return { count: results.count, items: results.users };
      } catch (err) {
        handleError(err);
        return { count: 0, items: [] };
      }
    },
    initial: true,
    itemsPerPage: limit,
  });

  // Reset pagination when search params change
  useEffect(() => {
    if (offset !== 0) setOffset(0);
  }, [query, languageCode, learning, minProficiency, maxProficiency, limit, searchType, advancedMode]);

  // Form handlers
  const handleSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    searchResults.refresh();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === "checkbox";

    switch (name) {
      case "query":
        setQuery(value);
        break;
      case "languageCode":
        setLanguageCode(value);
        break;
      case "minProficiency":
        setMinProficiency(value);
        break;
      case "maxProficiency":
        setMaxProficiency(value);
        break;
      case "limit":
        setLimit(Number(value));
        break;
      case "searchType":
        setSearchType(value);
        break;
      case "learning":
        isCheckbox && setLearning((e.target as HTMLInputElement).checked);
        break;
    }
  };

  const handleLanguageSelect = (language: Language) => {
    setLanguageCode(language.code);
  };

  const handleLanguageClear = () => {
    setLanguageCode("");
  };

  const toggleAdvancedMode = () => setAdvancedMode(!advancedMode);

  // Pagination helpers
  const pageNumbers = useMemo(() => {
    const totalPages = Math.ceil(searchResults.count / limit);
    const currentPage = Math.floor(offset / limit) + 1;
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) startPage = Math.max(1, endPage - maxPagesToShow + 1);

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }, [searchResults.count, limit, offset]);

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setOffset(newOffset);
  };

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(searchResults.count / limit);

  return (
    <div className="min-h-screen flex flex-col bg-white text-gray-800">
      <Header />

      <main className="flex-1 max-w-screen-lg mx-auto px-4 py-8 w-full">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Find Language Partners</h1>

        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-8">
          <form onSubmit={handleSearchSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="languageCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Language
                </label>
                <LanguageInput
                  value={languageCode}
                  onSelect={handleLanguageSelect}
                  onClear={handleLanguageClear}
                  placeholder="All Languages"
                  className="w-full"
                />
              </div>

              {!advancedMode && (
                <div>
                  <label htmlFor="searchType" className="block text-sm font-medium text-gray-700 mb-1">
                    I'm looking for
                  </label>
                  <select
                    id="searchType"
                    name="searchType"
                    value={searchType}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                  >
                    <option value="natives">Native speakers</option>
                    <option value="learners">Other learners</option>
                  </select>
                </div>
              )}
            </div>

            {advancedMode && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label htmlFor="query" className="block text-sm font-medium text-gray-700 mb-1">
                      Search by Name
                    </label>
                    <input
                      type="text"
                      id="query"
                      name="query"
                      value={query}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      placeholder="Enter name..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="learning"
                      name="learning"
                      checked={learning}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="learning" className="ml-2 block text-sm text-gray-700">
                      Learning this language
                    </label>
                  </div>

                  <div>
                    <label htmlFor="minProficiency" className="block text-sm font-medium text-gray-700 mb-1">
                      Min Proficiency
                    </label>
                    <select
                      id="minProficiency"
                      name="minProficiency"
                      value={minProficiency}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    >
                      <option value="">Any</option>
                      <option value="0.1">Beginner</option>
                      <option value="0.4">Intermediate</option>
                      <option value="0.7">Fluent</option>
                      <option value="0.9">Native</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="maxProficiency" className="block text-sm font-medium text-gray-700 mb-1">
                      Max Proficiency
                    </label>
                    <select
                      id="maxProficiency"
                      name="maxProficiency"
                      value={maxProficiency}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    >
                      <option value="">Any</option>
                      <option value="0.3">Beginner</option>
                      <option value="0.6">Intermediate</option>
                      <option value="0.8">Fluent</option>
                      <option value="1.0">Native</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={toggleAdvancedMode}
                className="text-primary hover:text-primary-light font-medium text-sm focus:outline-none"
              >
                {advancedMode ? "Simple Search" : "Advanced Search Options"}
              </button>
              <button
                type="submit"
                disabled={searchResults.isLoading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Search Results */}
        <div className="space-y-6">
          {searchResults.isLoading && searchResults.data.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-gray-500">Searching for language partners...</div>
            </div>
          ) : searchResults.data.length > 0 ? (
            <>
              {isLoggedIn && (
                <p className="text-gray-600">
                  Showing {offset + 1}-{Math.min(offset + limit, searchResults.count)} of {searchResults.count} results
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {searchResults.data.slice(0, isLoggedIn ? undefined : 9).map((user) => (
                  <UserCard key={user.userId} user={user} />
                ))}

                {/* Include promo card in the grid for non-logged in users */}
                {!isLoggedIn && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition">
                    <div className="p-6 flex h-full flex-col items-start">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Discover More Language Partners</h3>
                      <p className="text-gray-600 text-sm mb-4">
                        Connect with thousands of people from over 40 countries speaking multiple languages.
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {["Spanish", "French", "Japanese", "Korean"].map((lang) => (
                          <span key={lang} className="px-2 py-1 bg-blue-50 text-blue-800 text-xs rounded-full">
                            {lang}
                          </span>
                        ))}
                      </div>
                      <div className="flex-1" />
                      <a
                        href="/signup"
                        className="inline-block px-4 py-2 bg-primary text-white rounded-md shadow hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 text-sm"
                      >
                        Create Free Account
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Pagination - only show for logged in users */}
              {isLoggedIn && searchResults.count > limit && (
                <div className="mt-6 flex items-center justify-center">
                  <nav className="flex items-center space-x-1" aria-label="Pagination">
                    <button
                      onClick={() => changePage(currentPage - 1)}
                      disabled={currentPage === 1 || searchResults.isLoading}
                      className={`p-2 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                        currentPage === 1 || searchResults.isLoading
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                      }`}
                      aria-label="Previous page"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                      </svg>
                    </button>

                    {pageNumbers.map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => changePage(pageNum)}
                        disabled={searchResults.isLoading}
                        className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium 
                          transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                            pageNum === currentPage
                              ? "bg-blue-500 text-white"
                              : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                          }`}
                        aria-current={pageNum === currentPage ? "page" : undefined}
                      >
                        {pageNum}
                      </button>
                    ))}

                    <button
                      onClick={() => changePage(currentPage + 1)}
                      disabled={currentPage === totalPages || searchResults.isLoading}
                      className={`p-2 rounded-full transition-colors focus:outline-none focus:ring-2 
                        focus:ring-blue-500 focus:ring-opacity-50 ${
                          currentPage === totalPages || searchResults.isLoading
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                        }`}
                      aria-label="Next page"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </button>
                  </nav>
                </div>
              )}

              {searchResults.isLoading && (
                <div className="mt-4 text-center py-2">
                  <div className="inline-flex items-center text-gray-500">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading...
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-8 text-center">
              <p className="text-gray-600">No users found matching your criteria.</p>
              <p className="text-gray-500 text-sm mt-2">Try adjusting your search filters.</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function UserCard({ user }: { user: User.SearchReturn["users"][0] }) {
  const languages = user.languages || [];

  const speakingLanguages = languages
    .filter((lang) => !lang.learning)
    .map((lang) => ({
      code: lang.languageCode,
      name: getLanguageName(lang.languageCode),
      proficiency: getProficiencyLevel(lang.proficiency),
    }));

  const learningLanguages = languages
    .filter((lang) => lang.learning)
    .map((lang) => ({
      code: lang.languageCode,
      name: getLanguageName(lang.languageCode),
      proficiency: getProficiencyLevel(lang.proficiency),
    }));

  const formatLastActive = () => {
    if (!user.lastActiveAt) return null;

    const lastActive = new Date(user.lastActiveAt);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - lastActive.getTime()) / (1000 * 60));

    if (diffMinutes < 15) return "Active now";

    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    if (lastActive >= today) return "Active today";

    return null;
  };

  const lastActiveText = formatLastActive();

  const truncateDescription = (text: string, maxLength: number = 90) =>
    text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition">
      <div className="p-6">
        <div className="flex items-center mb-4">
          <a href={`/profile/${user.userId}`} className="w-12 mr-4">
            {user.profileImageUrl ? (
              <img
                src={user.profileImageUrl}
                alt={`${user.name}'s avatar`}
                className="w-12 h-12 rounded-full object-cover hover:opacity-90 transition-opacity"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors">
                <span className="text-gray-500 text-lg font-medium">{user.name?.charAt(0).toUpperCase() || "?"}</span>
              </div>
            )}
          </a>
          <div className="flex-1">
            <div className="flex items-center">
              <a href={`/profile/${user.userId}`} className="hover:text-primary">
                <h3 className="text-lg font-semibold text-gray-800">{user.name || "Anonymous"}</h3>
              </a>
              {lastActiveText && (
                <span className="ml-2 text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full">
                  {lastActiveText}
                </span>
              )}
            </div>
            <p className="text-gray-500 text-sm">
              {user.description ? truncateDescription(user.description) : "No description provided"}
            </p>
          </div>
        </div>

        {(speakingLanguages.length > 0 || learningLanguages.length > 0) && (
          <div className="mt-4">
            {speakingLanguages.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-1">Speaks:</p>
                <div className="flex flex-wrap gap-2">
                  {speakingLanguages.map((lang) => (
                    <span key={lang.code} className="px-2 py-1 bg-green-50 text-green-800 text-xs rounded-full">
                      {lang.name} ({lang.proficiency})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {learningLanguages.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Learning:</p>
                <div className="flex flex-wrap gap-2">
                  {learningLanguages.map((lang) => (
                    <span key={lang.code} className="px-2 py-1 bg-blue-50 text-blue-800 text-xs rounded-full">
                      {lang.name} ({lang.proficiency})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          <a href={`/profile/${user.userId}`} className="text-primary hover:text-primary-light font-medium text-sm">
            View Profile →
          </a>
        </div>
      </div>
    </div>
  );
}
